import {
  normalizeRatingKey,
  ratingKeyFromScore,
  ratingLabel,
} from '@src/components/sco/PitchDeckAuditor/model/ratingMeta'
import {
  CoherenceItemView,
  CriticalIssueView,
  PitchDeckStats,
  PitchDeckView,
  PitchIssueView,
  PitchSlideView,
  RatingKey,
  RewriteAnnotation,
} from '@src/components/sco/PitchDeckAuditor/model/types'
import { ScoPitchDeck, ScoPitchDeckAuditSlide, ScoPitchDeckSlide } from '@src/graphql-types'

const CRITICAL_WEIGHT_THRESHOLD = 20
const QUICK_WIN_WEIGHT_THRESHOLD = 10
const MAX_CRITICAL_ISSUES = 6

export type PitchDeckStaticLookups = {
  ruleTitleById: Record<string, string>
  slideTypeLabelByKey: Record<string, string>
  coherenceLabelByKey: Record<string, string>
  coherenceOrder: string[]
}

export const EMPTY_LOOKUPS: PitchDeckStaticLookups = {
  ruleTitleById: {},
  slideTypeLabelByKey: {},
  coherenceLabelByKey: {},
  coherenceOrder: [],
}

const PROCESSING_STATUSES = new Set(['uploaded', 'parsing', 'classified', 'auditing'])

export function formatFileSize(bytes?: number | null): string | null {
  if (!bytes || bytes <= 0) {
    return null
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  const kb = bytes / 1024
  if (kb < 1024) {
    return `${kb.toFixed(0)} KB`
  }
  const mb = kb / 1024
  return `${mb.toFixed(1)} MB`
}

function adaptIssue(item: any, lookups: PitchDeckStaticLookups): PitchIssueView {
  const ruleTitle = item.ruleId ? (lookups.ruleTitleById[item.ruleId] ?? null) : null
  return {
    id: item.id ?? item.pitchDeckAuditSlideItemId,
    body: item.body ?? '',
    ruleId: item.ruleId ?? null,
    ruleTitle,
    ruleWeight: item.ruleWeight ?? null,
    targetPhrase: item.targetPhrase ?? null,
  }
}

function buildAnnotations(issues: PitchIssueView[]): RewriteAnnotation[] {
  return issues
    .filter(issue => !!issue.targetPhrase)
    .map(issue => ({
      phrase: issue.targetPhrase as string,
      label: issue.ruleTitle || issue.ruleId || 'Issue',
    }))
}

function adaptSlide(
  slide: ScoPitchDeckSlide,
  auditSlide: ScoPitchDeckAuditSlide | undefined,
  lookups: PitchDeckStaticLookups
): PitchSlideView {
  const items = (auditSlide?.items ?? []).filter(Boolean) as any[]
  const issues = items
    .filter(item => item.itemTypeKey === 'issue')
    .map(item => adaptIssue(item, lookups))
    .sort((a, b) => (b.ruleWeight ?? 0) - (a.ruleWeight ?? 0))
  const whyThisWorks = items
    .filter(item => item.itemTypeKey === 'whyThisWorks')
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(item => item.body)
    .filter(Boolean)

  const isAudited = !!auditSlide
  const ratingKey: RatingKey = isAudited
    ? normalizeRatingKey(auditSlide?.ratingKey, auditSlide?.ratingLabel)
    : 'notAudited'

  return {
    pitchDeckSlideId: slide.pitchDeckSlideId as string,
    position: slide.position ?? 0,
    name: slide.slideTitle || slide.slideName || `Slide ${slide.position ?? ''}`.trim(),
    code: slide.slideCode ?? auditSlide?.slideCode ?? null,
    classificationKey: slide.classificationKey ?? null,
    classificationLabel: slide.classificationKey
      ? (lookups.slideTypeLabelByKey[slide.classificationKey] ?? null)
      : null,

    isAudited,
    auditSlideId: auditSlide?.pitchDeckAuditSlideId ?? null,
    ratingKey,
    ratingLabel: ratingLabel(ratingKey, auditSlide?.ratingLabel),
    score: typeof auditSlide?.score === 'number' ? auditSlide.score : null,

    slideImageUrl: slide.slideImageUrl ?? null,
    currentText: slide.currentText ?? auditSlide?.whatTheySaid ?? null,
    whatTheySaid: auditSlide?.whatTheySaid ?? null,
    whatWorks: auditSlide?.whatWorks ?? null,
    whatsMissingOrBroken: auditSlide?.whatsMissingOrBroken ?? null,
    investorObjection: auditSlide?.investorObjection ?? null,
    coherenceNote: auditSlide?.coherenceNote ?? null,

    issues,
    whyThisWorks,
    suggestedRewrite: auditSlide?.suggestedRewrite ?? null,
    acceptedRewrite: auditSlide?.acceptedRewrite ?? null,
    rewriteAnnotations: buildAnnotations(issues),

    threadId: (slide.toolSession?.threadId as string) ?? null,
    toolSessionId: slide.toolSession?.toolSessionId ?? null,
  }
}

function computeStats(auditSlides: PitchSlideView[]): PitchDeckStats {
  let problems = 0
  let critical = 0
  let quickWins = 0

  auditSlides.forEach(slide => {
    slide.issues.forEach(issue => {
      problems += 1
      const weight = issue.ruleWeight ?? 0
      const onBrokenSlide = slide.ratingKey === 'fundamentalRework'
      if (weight >= CRITICAL_WEIGHT_THRESHOLD || (onBrokenSlide && weight === 0)) {
        critical += 1
      } else if (weight > 0 && weight <= QUICK_WIN_WEIGHT_THRESHOLD) {
        quickWins += 1
      }
    })
  })

  return { problems, critical, quickWins }
}

function collectCriticalIssues(slides: PitchSlideView[]): CriticalIssueView[] {
  const all: CriticalIssueView[] = []
  slides.forEach(slide => {
    slide.issues.forEach(issue => {
      all.push({
        ...issue,
        pitchDeckSlideId: slide.pitchDeckSlideId,
        slideName: slide.name,
        slideRatingKey: slide.ratingKey,
        classificationLabel: slide.classificationLabel ?? null,
      })
    })
  })
  return all.sort((a, b) => (b.ruleWeight ?? 0) - (a.ruleWeight ?? 0)).slice(0, MAX_CRITICAL_ISSUES)
}

function adaptCoherence(
  crossSlideCoherenceAsJson: Record<string, unknown> | null | undefined,
  lookups: PitchDeckStaticLookups
): CoherenceItemView[] {
  if (!crossSlideCoherenceAsJson || typeof crossSlideCoherenceAsJson !== 'object') {
    return []
  }
  const json = crossSlideCoherenceAsJson as Record<string, unknown>
  const keys =
    lookups.coherenceOrder.length > 0 ? lookups.coherenceOrder.filter(key => key in json) : Object.keys(json)
  // include any keys present in the data but missing from the static order
  Object.keys(json).forEach(key => {
    if (!keys.includes(key)) {
      keys.push(key)
    }
  })
  return keys
    .map(key => ({
      key,
      label: lookups.coherenceLabelByKey[key] ?? key,
      body: typeof json[key] === 'string' ? (json[key] as string) : String(json[key] ?? ''),
    }))
    .filter(item => item.body.trim().length > 0)
}

export function adaptDeck(
  deck: ScoPitchDeck | null | undefined,
  lookups: PitchDeckStaticLookups = EMPTY_LOOKUPS
): PitchDeckView | null {
  if (!deck) {
    return null
  }

  const audit = deck.latestPitchDeckAudit
  const auditSlides = (audit?.slides ?? []).filter(Boolean) as ScoPitchDeckAuditSlide[]

  const auditBySlideId: Record<string, ScoPitchDeckAuditSlide> = {}
  const auditByKey: Record<string, ScoPitchDeckAuditSlide> = {}
  auditSlides.forEach(auditSlide => {
    if (auditSlide.pitchDeckSlideId) {
      auditBySlideId[auditSlide.pitchDeckSlideId] = auditSlide
    }
    if (auditSlide.slideKey) {
      auditByKey[auditSlide.slideKey] = auditSlide
    }
  })

  const parsedSlides = (deck.slides ?? []).filter(Boolean) as ScoPitchDeckSlide[]
  const slides = parsedSlides
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(slide => {
      const auditSlide =
        (slide.pitchDeckSlideId && auditBySlideId[slide.pitchDeckSlideId]) ||
        (slide.classificationKey && auditByKey[slide.classificationKey]) ||
        undefined
      return adaptSlide(slide, auditSlide, lookups)
    })

  const statusKey = deck.statusKey ?? 'uploaded'
  const hasStoredFile = !!(deck.deckStoredFileId || deck.deckStoredFile?.storedFileId)
  const isFailed = statusKey === 'failed'
  const isAudited = statusKey === 'audited' && !!audit
  // Empty shells (no PDF yet) must not enter the processing/auditor UX.
  const isProcessing = hasStoredFile && !isFailed && !isAudited && PROCESSING_STATUSES.has(statusKey)

  const overallScore = typeof audit?.overallScore === 'number' ? audit.overallScore : null
  const overallRatingKey = ratingKeyFromScore(overallScore)

  const errorAsJson = deck.errorAsJson as Record<string, unknown> | null | undefined
  const errorMessage =
    errorAsJson && typeof errorAsJson === 'object'
      ? (errorAsJson.message as string) || (errorAsJson.error as string) || null
      : null

  return {
    pitchDeckId: deck.pitchDeckId as string,
    title: deck.title || audit?.companyName || 'Pitch deck',
    companyName: audit?.companyName || deck.title || 'Your company',

    hasStoredFile,

    fileName: deck.deckStoredFile?.title || deck.deckStoredFile?.storageFileName || null,
    fileSizeLabel: formatFileSize(deck.deckStoredFile?.storageFileSizeInBytes),
    fileUrl: deck.deckStoredFile?.fullUrl ?? null,

    slideCount: deck.slideCount ?? parsedSlides.length,
    statusKey,
    phaseKey: deck.phaseKey ?? null,
    isProcessing,
    isFailed,
    isAudited,
    errorMessage,

    auditAiActionId: deck.auditAiActionId ?? null,
    classifyAiActionId: deck.classifyAiActionId ?? null,

    auditId: audit?.pitchDeckAuditId ?? deck.latestPitchDeckAuditId ?? null,
    auditStatusKey: audit?.statusKey ?? null,
    overallScore,
    overallRatingKey,
    verdict: audit?.wilVerdict ?? null,
    overview: audit?.overallSummary ?? null,
    analyzedAt: (audit?.analyzedAt as string) ?? (deck.analyzedAt as string) ?? null,

    stats: computeStats(slides),
    criticalIssues: collectCriticalIssues(slides),
    coherence: adaptCoherence(audit?.crossSlideCoherenceAsJson as Record<string, unknown>, lookups),
    slides,

    deckThreadId: (deck.overallToolSession?.threadId as string) ?? null,
    deckToolSessionId: deck.overallToolSession?.toolSessionId ?? null,
  }
}
